import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { LoadingSpinner } from '../../../../../web/components/LoadingSpinner';
import { Modal } from '../../../../../web/components/Modal';
import { Input } from '../../../../../web/components-v2/Input';
import { PricingCalculationResponse } from '../types';
import { styleSheet } from './styles';
import { INDUSTRIES, ProposalFormData, ProposalType, WEBSITE_ADDONS } from './types';
import { useProposalGeneration } from './useProposalGeneration';

interface IProps {
	isOpen: boolean;
	onRequestClose: () => void;
	pricingData: PricingCalculationResponse;
}

export function GenerateProposalModal({ isOpen, onRequestClose, pricingData }: IProps) {
	const [proposalType, setProposalType] = React.useState<ProposalType>('standard');
	const [industry, setIndustry] = React.useState('');
	const [special, setSpecial] = React.useState('');
	const [prospectName, setProspectName] = React.useState('');
	const [prospectTitle, setProspectTitle] = React.useState('');
	const [companyName, setCompanyName] = React.useState('');
	const [websiteAddon, setWebsiteAddon] = React.useState('');
	const [generatedFileId, setGeneratedFileId] = React.useState<string | null>(null);
	const [generatedPdfUrl, setGeneratedPdfUrl] = React.useState<string | null>(null);
	const [generatedFileName, setGeneratedFileName] = React.useState<string | null>(null);

	const proposalMutation = useProposalGeneration();

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		if (proposalType === 'standard' && !industry) {
			toast.error('Please select an industry');
			return;
		}

		if (proposalType === 'custom' && !prospectName) {
			toast.error('Please enter prospect name');
			return;
		}

		const formData: ProposalFormData = {
			companyName,
			industry,
			proposalType,
			prospectName,
			prospectTitle,
			special,
			websiteAddon,
		};

		proposalMutation.mutate(
			{ ...formData, pricingData },
			{
				onError: error => {
					toast.error(`Failed to generate proposal: ${error.message}`);
				},
				onSuccess: data => {
					if (data.success) {
						setGeneratedFileId(data.fileId || null);
						setGeneratedPdfUrl(data.pdfUrl || null);
						setGeneratedFileName(data.fileName || 'Proposal');
						toast.success('Proposal generated successfully!');
					} else {
						toast.error(`Failed to generate proposal: ${data.error || 'Unknown error'}`);
					}
				},
			}
		);
	};

	const handleClose = () => {
		// Reset form
		setProposalType('standard');
		setIndustry('');
		setSpecial('');
		setProspectName('');
		setProspectTitle('');
		setCompanyName('');
		setWebsiteAddon('');
		setGeneratedFileId(null);
		setGeneratedPdfUrl(null);
		setGeneratedFileName(null);
		onRequestClose();
	};

	const formatCurrency = (value: number | undefined): string => {
		const numericValue = value ?? 0;
		return numericValue.toLocaleString('en-US', {
			maximumFractionDigits: 2,
			minimumFractionDigits: 2,
		});
	};

	return (
		<Modal isOpen={isOpen} onRequestClose={handleClose} useDefaultHeader={true}>
			<div className={css(styleSheet.container)}>
				<h2 className={css(styleSheet.title)}>Generate Proposal</h2>

				{/* Price Summary */}
				<div className={css(styleSheet.priceSummary)}>
					<div>
						<strong>Plan:</strong> {pricingData.planType || 'N/A'}
					</div>
					<div>
						<strong>Users:</strong> {pricingData.numUsers || 'N/A'}
					</div>
					<div>
						<strong>Annual Total:</strong> ${formatCurrency(pricingData.annualTotal)}
					</div>
					<div>
						<strong>Billing Cycle:</strong> {pricingData.billingCycle || 'Annual'}
					</div>
				</div>

				{!generatedFileId && (
					<>
						{/* Proposal Type Selection */}
						<div className={css(styleSheet.proposalTypeSection)}>
							<label className={css(styleSheet.radioLabel)}>
								<input
									checked={proposalType === 'standard'}
									name='proposalType'
									onChange={() => setProposalType('standard')}
									type='radio'
									value='standard'
								/>
								Standard Proposal
							</label>
							<label className={css(styleSheet.radioLabel)}>
								<input
									checked={proposalType === 'custom'}
									name='proposalType'
									onChange={() => setProposalType('custom')}
									type='radio'
									value='custom'
								/>
								Custom Proposal
							</label>
						</div>

						<form onSubmit={handleSubmit}>
							{/* Standard Proposal Fields */}
							{proposalType === 'standard' && (
								<div className={css(styleSheet.formSection)}>
									<h3 className={css(styleSheet.sectionTitle)}>Industry</h3>
									<div className={css(styleSheet.formGroup)}>
										<label className={css(styleSheet.label)} htmlFor='industry'>
											Select Industry *
										</label>
										<select
											className={css(styleSheet.select)}
											id='industry'
											onChange={e => setIndustry(e.target.value)}
											required
											value={industry}
										>
											<option value=''>Select Industry</option>
											{INDUSTRIES.map(ind => (
												<option key={ind.value} value={ind.value}>
													{ind.label}
												</option>
											))}
										</select>
									</div>

									<div className={css(styleSheet.formGroup)}>
										<label className={css(styleSheet.label)} htmlFor='special'>
											Special:
										</label>
										<select
											className={css(styleSheet.select)}
											id='special'
											onChange={e => setSpecial(e.target.value)}
											value={special}
										>
											<option value=''>-- None --</option>
											<option value='Cetera'>Cetera</option>
										</select>
									</div>
								</div>
							)}

							{/* Custom Proposal Fields */}
							{proposalType === 'custom' && (
								<div className={css(styleSheet.formSection)}>
									<h3 className={css(styleSheet.sectionTitle)}>Prospect Information</h3>
									<div className={css(styleSheet.formGroup)}>
										<label className={css(styleSheet.label)} htmlFor='prospectName'>
											Prospect Name *
										</label>
										<Input
											styles={[styleSheet.input]}
											id='prospectName'
											onChange={e => setProspectName(e.target.value)}
											required
											type='text'
											value={prospectName}
										/>
									</div>

									<div className={css(styleSheet.formGroup)}>
										<label className={css(styleSheet.label)} htmlFor='prospectTitle'>
											Prospect Title
										</label>
										<Input
											styles={[styleSheet.input]}
											id='prospectTitle'
											onChange={e => setProspectTitle(e.target.value)}
											type='text'
											value={prospectTitle}
										/>
									</div>

									<div className={css(styleSheet.formGroup)}>
										<label className={css(styleSheet.label)} htmlFor='companyName'>
											Company Name
										</label>
										<Input
											styles={[styleSheet.input]}
											id='companyName'
											onChange={e => setCompanyName(e.target.value)}
											type='text'
											value={companyName}
										/>
									</div>
								</div>
							)}

							{/* Website Add-on (show for Preferred Website plans) */}
							<div className={css(styleSheet.formSection)}>
								<h3 className={css(styleSheet.sectionTitle)}>Website Add-on</h3>
								<div className={css(styleSheet.formGroup)}>
									<label className={css(styleSheet.label)} htmlFor='websiteAddon'>
										Select Add-on for Preferred Website
									</label>
									<select
										className={css(styleSheet.select)}
										id='websiteAddon'
										onChange={e => setWebsiteAddon(e.target.value)}
										value={websiteAddon}
									>
										{WEBSITE_ADDONS.map(addon => (
											<option key={addon.value} value={addon.value}>
												{addon.label}
											</option>
										))}
									</select>
								</div>
							</div>

							{/* Action Buttons */}
							<div className={css(styleSheet.buttonRow)}>
								<button
									className={css(styleSheet.cancelButton)}
									disabled={proposalMutation.isPending}
									onClick={handleClose}
									type='button'
								>
									Cancel
								</button>
								<button className={css(styleSheet.generateButton)} disabled={proposalMutation.isPending} type='submit'>
									{proposalMutation.isPending ? (
										<>
											<LoadingSpinner type='tiny' /> Generating...
										</>
									) : (
										'Generate Proposal'
									)}
								</button>
							</div>
						</form>
					</>
				)}

				{/* Success Section */}
				{generatedFileId && (
					<div className={css(styleSheet.successSection)}>
						<h3 className={css(styleSheet.successTitle)}>✅ Proposal Generated Successfully!</h3>
						<p className={css(styleSheet.fileName)}>{generatedFileName}.pdf</p>
						<div className={css(styleSheet.downloadButtons)}>
							{generatedPdfUrl && (
								<a
									className={css(styleSheet.downloadButton)}
									download={`${generatedFileName}.pdf`}
									href={generatedPdfUrl}
									rel='noopener noreferrer'
									target='_blank'
								>
									📄 Download PDF
								</a>
							)}
							{generatedFileId && (
								<a
									className={css(styleSheet.downloadButton)}
									href={`https://docs.google.com/presentation/d/${generatedFileId}/edit`}
									rel='noopener noreferrer'
									target='_blank'
								>
									📊 Open in Google Slides
								</a>
							)}
						</div>
						<button className={css(styleSheet.closeButton)} onClick={handleClose} type='button'>
							Close
						</button>
					</div>
				)}
			</div>
		</Modal>
	);
}
